import { EVENTS_JSON_PATH } from '#shared/constants';
import type { EventInput, EventRecord, EventsSnapshot } from '#shared/types';
import {
  dummyCreateEvent,
  dummyDeleteEvent,
  dummyFetchAdminEvents,
  dummyPublishEvent,
  dummyUnpublishEvent,
  dummyUpdateEvent,
} from '#src/api/dummyAdminStore';
import { DUMMY_EVENTS_SNAPSHOT } from '#src/api/dummyEvents';
import { loadRuntimeConfig } from '#src/config';
import { isLocalDemoMode } from '#src/utils/localDemo';

const getApiUrl = async (): Promise<string> => {
  const config = await loadRuntimeConfig();
  return config.apiUrl;
};

const authHeaders = (token: string) => ({
  Authorization: `Bearer ${token}`,
  'Content-Type': 'application/json',
});

export const fetchEvents = async (): Promise<EventsSnapshot> => {
  if (process.env.NODE_ENV === 'development') {
    return DUMMY_EVENTS_SNAPSHOT;
  }

  const response = await fetch(EVENTS_JSON_PATH);
  if (!response.ok) {
    throw new Error('Failed to load events');
  }
  return response.json() as Promise<EventsSnapshot>;
};

export const fetchAdminEvents = async (
  token: string,
  status: 'draft' | 'published',
): Promise<EventRecord[]> => {
  if (isLocalDemoMode()) {
    return dummyFetchAdminEvents(status);
  }

  const apiUrl = await getApiUrl();
  const response = await fetch(
    `${apiUrl}/admin/events?status=${encodeURIComponent(status)}`,
    { headers: authHeaders(token) },
  );
  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.error ?? 'Failed to load events');
  }
  return (data as { events: EventRecord[] }).events;
};

export const createEvent = async (
  token: string,
  input: EventInput,
): Promise<EventRecord> => {
  if (isLocalDemoMode()) {
    return dummyCreateEvent(input);
  }

  const apiUrl = await getApiUrl();
  const response = await fetch(`${apiUrl}/admin/events`, {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify(input),
  });
  const data = await response.json();
  if (!response.ok) {
    throw data;
  }
  return (data as { event: EventRecord }).event;
};

export const updateEvent = async (
  token: string,
  id: string,
  input: EventInput,
): Promise<void> => {
  if (isLocalDemoMode()) {
    await dummyUpdateEvent(id, input);
    return;
  }

  const apiUrl = await getApiUrl();
  const response = await fetch(`${apiUrl}/admin/events/${id}`, {
    method: 'PUT',
    headers: authHeaders(token),
    body: JSON.stringify(input),
  });
  const data = await response.json();
  if (!response.ok) {
    throw data;
  }
};

export const publishEvent = async (
  token: string,
  id: string,
  input: EventInput,
): Promise<void> => {
  if (isLocalDemoMode()) {
    await dummyPublishEvent(id, input);
    return;
  }

  const apiUrl = await getApiUrl();
  const response = await fetch(`${apiUrl}/admin/events/${id}/publish`, {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify(input),
  });
  const data = await response.json();
  if (!response.ok) {
    throw data;
  }
};

export const unpublishEvent = async (
  token: string,
  id: string,
): Promise<void> => {
  if (isLocalDemoMode()) {
    await dummyUnpublishEvent(id);
    return;
  }

  const apiUrl = await getApiUrl();
  const response = await fetch(`${apiUrl}/admin/events/${id}/unpublish`, {
    method: 'POST',
    headers: authHeaders(token),
  });
  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.error ?? 'Failed to unpublish event');
  }
};

export const deleteEvent = async (token: string, id: string): Promise<void> => {
  if (isLocalDemoMode()) {
    await dummyDeleteEvent(id);
    return;
  }

  const apiUrl = await getApiUrl();
  const response = await fetch(`${apiUrl}/admin/events/${id}`, {
    method: 'DELETE',
    headers: authHeaders(token),
  });
  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.error ?? 'Failed to delete event');
  }
};
